import React from 'react';
import { Pressable, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

const TOGGLE_WIDTH = 44;
const TOGGLE_HEIGHT = 24;
const THUMB_SIZE = 20;
const THUMB_MARGIN = 2;

const Toggle = ({ value = false, onValueChange, disabled = false, testID, accessibilityLabel }) => {
    const translateX = useSharedValue(value ? TOGGLE_WIDTH - THUMB_SIZE - THUMB_MARGIN * 2 : 0);

    React.useEffect(() => {
        translateX.value = withTiming(value ? TOGGLE_WIDTH - THUMB_SIZE - THUMB_MARGIN * 2 : 0, { duration: 200 });
    }, [value]);

    const thumbStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: translateX.value }],
    }));

    const handlePress = () => {
        if (disabled) return;
        onValueChange?.(!value);
    };

    return (
        <Pressable
            onPress={handlePress}
            disabled={disabled}
            style={styles.container(disabled)}
            accessibilityRole="switch"
            accessibilityLabel={accessibilityLabel}
            accessibilityState={{ checked: value, disabled }}
            testID={testID}
        >
            <View style={styles.track(value, disabled)}>
                <Animated.View style={[styles.thumb(value), thumbStyle]} />
            </View>
        </Pressable>
    );
};

const styles = StyleSheet.create(({ colors, shadows }) => ({
    container: (disabled) => ({
        opacity: disabled ? 0.5 : 1,
        borderRadius: TOGGLE_HEIGHT / 2,
        outlineStyle: 'none',
        _web: {
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'box-shadow 0.15s ease',
            _focus: {
                boxShadow: disabled ? 'none' : `0 0 0 3px ${colors.primary}26`,
            },
            _hover: {
                boxShadow: disabled ? 'none' : `0 0 0 3px ${colors.hoverStrong}`,
            },
        },
    }),
    track: (isOn, disabled) => ({
        width: TOGGLE_WIDTH,
        height: TOGGLE_HEIGHT,
        borderRadius: TOGGLE_HEIGHT / 2,
        backgroundColor: isOn ? colors.primary : colors.border,
        justifyContent: 'center',
        padding: THUMB_MARGIN,
        _web: {
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: disabled ? (isOn ? colors.primary : colors.border) : (isOn ? colors.primaryMuted : colors.outline),
            },
        },
    }),
    thumb: (_isOn) => ({
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: THUMB_SIZE / 2,
        backgroundColor: '#ffffff',
        boxShadow: shadows.header,
    }),
}));

export default Toggle;
